import { HTMLAttributeReferrerPolicy } from "react";

/**
 * Configuration options for image loading strategy.
 */
export interface ImageLoadingOptions {
  /** Indicates if the image is critical for initial viewport rendering (LCP). */
  priority?: boolean;
  /** Index of the image in a list or carousel. Index 0 defaults to eager/priority. */
  index?: number;
  /** Whether the image is located above the fold. */
  aboveTheFold?: boolean;
  /** Whether the image is used as a background image. */
  isBackground?: boolean;
  /** Fetch priority hint for browser network scheduling. */
  fetchPriority?: "high" | "low" | "auto";
}

/**
 * Standardized HTML image element loading attributes.
 */
export interface ImageAttributes {
  loading: "lazy" | "eager";
  decoding: "async" | "sync";
  fetchPriority: "high" | "low" | "auto";
  referrerPolicy: HTMLAttributeReferrerPolicy;
}

/**
 * Cache for pre-decoded images to prevent duplicate network requests and decoding overhead.
 */
const imageCache = new Set<string>();

/**
 * Determines optimal browser image loading attributes based on position and priority.
 * Utilizing `loading="lazy"` and `decoding="async"` prevents main-thread layout thrashing
 * when rendering large appliance catalog grids.
 */
export function getImageLoadingProps(options: ImageLoadingOptions = {}): ImageAttributes {
  const isPriority = Boolean(
    options.priority || options.aboveTheFold || (typeof options.index === "number" && options.index === 0)
  );

  return {
    loading: isPriority ? "eager" : "lazy",
    decoding: "async",
    fetchPriority: options.fetchPriority || (isPriority ? "high" : "low"),
    referrerPolicy: "no-referrer",
  };
}

/**
 * Asynchronously pre-decodes high-resolution product images or AI-generated product backgrounds
 * off the main UI thread using the native `HTMLImageElement.decode()` API.
 * This prevents UI frame drops during smooth carousel transitions and page scrolls.
 */
export function preloadImage(
  url: string,
  options: { fetchPriority?: "high" | "low" | "auto" } = {}
): Promise<HTMLImageElement> {
  if (!url || typeof window === "undefined") {
    return Promise.reject(new Error("Invalid image URL or server environment"));
  }

  if (imageCache.has(url)) {
    const img = new Image();
    img.src = url;
    return Promise.resolve(img);
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.referrerPolicy = "no-referrer";
    
    // Set fetch priority if supported
    if ("fetchPriority" in img) {
      (img as any).fetchPriority = options.fetchPriority || "auto";
    }

    img.onload = () => {
      // Use native async decoding off the main thread if supported
      if ("decode" in img && typeof img.decode === "function") {
        img
          .decode()
          .then(() => {
            imageCache.add(url);
            resolve(img);
          })
          .catch(() => {
            // Fallback if decode rejects (e.g., format edge cases)
            imageCache.add(url);
            resolve(img);
          });
      } else {
        imageCache.add(url);
        resolve(img);
      }
    };

    img.onerror = (err) => {
      reject(err);
    };

    img.src = url;
  });
}

/**
 * Optimizes image URLs (Unsplash CDN, Google Drive, AI-generated background URLs)
 * with specific parameters to reduce payload size and serve WebP/AVIF formats.
 */
export function getOptimizedImageUrl(
  url: string | undefined,
  width: number = 600,
  quality: number = 60
): string {
  if (!url || url === "data:image/jpeg;base64" || url === "data:image/png;base64") {
    return `https://images.unsplash.com/photo-1571175432267-efb0258593a6?auto=format&fit=crop&w=${width}&q=${quality}`;
  }

  let cleanUrl = url.trim();

  // Enforce HTTPS protocol
  if (cleanUrl.startsWith("http://")) {
    cleanUrl = cleanUrl.replace("http://", "https://");
  }

  // Unsplash CDN Optimization
  if (cleanUrl.includes("unsplash.com")) {
    const baseUrl = cleanUrl.split("?")[0];
    return `${baseUrl}?auto=format&fit=crop&w=${width}&q=${quality}`;
  }

  // Google Drive CDN Optimization
  if (cleanUrl.includes("drive.google.com")) {
    const fileIdMatch = cleanUrl.match(/\/d\/([a-zA-Z0-9_-]+)/) || cleanUrl.match(/id=([a-zA-Z0-9_-]+)/);
    if (fileIdMatch && fileIdMatch[1]) {
      return `https://lh3.googleusercontent.com/d/${fileIdMatch[1]}=w${width}`;
    }
  }

  // AI-generated background / Cloudinary / ImageKit / Imgix optimization parameters
  if (cleanUrl.includes("cloudinary.com") && !cleanUrl.includes("f_auto")) {
    return cleanUrl.replace("/upload/", `/upload/f_auto,q_${quality},w_${width}/`);
  }

  return cleanUrl;
}

/**
 * Applies high-resolution AI-generated product background images to DOM elements asynchronously
 * after pre-decoding, preventing main-thread lag and visual layout shifts.
 */
export async function applyOptimizedBackground(
  element: HTMLElement | null,
  rawUrl: string,
  options: {
    width?: number;
    quality?: number;
    overlayGradient?: string;
    fallbackColor?: string;
  } = {}
): Promise<void> {
  if (!element || !rawUrl) return;

  const { width = 1200, quality = 70, overlayGradient, fallbackColor = "#111827" } = options;
  const optimizedUrl = getOptimizedImageUrl(rawUrl, width, quality);

  try {
    // Asynchronously pre-decode off main thread
    await preloadImage(optimizedUrl, { fetchPriority: "low" });

    // Smoothly set background image once fully decoded
    const bgValue = overlayGradient
      ? `${overlayGradient}, url("${optimizedUrl}")`
      : `url("${optimizedUrl}")`;

    element.style.backgroundImage = bgValue;
    element.style.backgroundSize = "cover";
    element.style.backgroundPosition = "center";
  } catch (error) {
    console.warn("Failed to load optimized background image:", error);
    if (fallbackColor) {
      element.style.backgroundColor = fallbackColor;
    }
  }
}

/**
 * Generates responsive srcset string for high-DPI screens and variable viewport sizes.
 */
export function generateSrcSet(url: string, widths: number[] = [320, 640, 960, 1280]): string {
  if (!url || url.startsWith("data:")) return "";

  return widths
    .map((w) => `${getOptimizedImageUrl(url, w, 60)} ${w}w`)
    .join(", ");
}

/**
 * Options for client-side image compression and upload processing.
 */
export interface ImageCompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  initialQuality?: number;
  targetMaxKb?: number;
  outputFormat?: "image/webp" | "image/jpeg";
}

/**
 * Client-side upload pipeline utility:
 * Converts raw uploaded product images to compressed WebP format (or JPEG fallback)
 * targeting <150KB for product detail images and <50KB for thumbnails.
 */
export async function compressAndOptimizeUpload(
  file: File,
  options: ImageCompressionOptions = {}
): Promise<string> {
  const {
    maxWidth = 900,
    maxHeight = 900,
    initialQuality = 0.8,
    targetMaxKb = 150,
    outputFormat = "image/webp",
  } = options;

  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith("image/")) {
      reject(new Error("Invalid image file type. Please upload a valid image (JPEG, PNG, WebP)."));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // Downscale while maintaining aspect ratio
        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(e.target?.result as string);
          return;
        }

        // Fill white background for transparent PNGs
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        // Try WebP canvas export
        let quality = initialQuality;
        let dataUrl = canvas.toDataURL(outputFormat, quality);

        // Fallback to JPEG if browser doesn't produce image/webp
        if (!dataUrl.startsWith("data:image/webp") && outputFormat === "image/webp") {
          dataUrl = canvas.toDataURL("image/jpeg", quality);
        }

        // Iteratively compress quality if data length exceeds target max KB
        const maxByteLength = targetMaxKb * 1024;
        let attempts = 0;
        while (dataUrl.length > maxByteLength * 1.35 && quality > 0.35 && attempts < 5) {
          quality -= 0.1;
          attempts++;
          dataUrl = canvas.toDataURL(
            dataUrl.startsWith("data:image/webp") ? "image/webp" : "image/jpeg",
            quality
          );
        }

        resolve(dataUrl);
      };

      img.onerror = () => {
        reject(new Error("Failed to load and decode image file."));
      };

      img.src = e.target?.result as string;
    };

    reader.onerror = () => reject(new Error("Error reading image file from disk."));
    reader.readAsDataURL(file);
  });
}

/**
 * Creates an IntersectionObserver tailored for pre-fetching and lazy-loading images or background cards
 * right before they enter the viewport.
 */
export function createLazyImageObserver(
  onIntersect: (element: HTMLElement) => void,
  options: IntersectionObserverInit = {}
): IntersectionObserver | null {
  if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
    return null;
  }

  const observerOptions: IntersectionObserverInit = {
    root: null,
    rootMargin: "200px 0px", // Preload 200px before reaching viewport
    threshold: 0.01,
    ...options,
  };

  const observer = new IntersectionObserver((entries, obs) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const target = entry.target as HTMLElement;
        onIntersect(target);
        obs.unobserve(target);
      }
    });
  }, observerOptions);

  return observer;
}
